import {
  Box,
  Container,
  Heading,
  HStack,
  Image,
  Text,
  useBreakpointValue,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import Blocks from '../../../assets/blocks.png';
import BuildingImageUrl from 'src/assets/images/Building.png';
import JoonaPhotoUrl from 'src/assets/images/Joona.jpg';
import { MerkatusLogo } from 'src/components/atoms/MerkatusLogo';
import { Icon } from 'src/components/atoms/Icon';

const CompanyPage: React.FC = () => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Box bg="white" fontFamily="marketingPage">
      <Box
        bg="text.darkBlue.900"
        color="white"
        justifyContent="center"
        display="flex"
        pt={{ base: 8, md: 16 }}
        flexDir="column"
        alignItems="center">
        <Container maxW="container.xl" textAlign="center">
          <Text
            fontSize={{ base: '3xl', md: '56px' }}
            fontWeight={700}
            fontFamily={'marketingPage'}>
            Merkatus.io is bringing
            <br /> clarity to marketing decisions
          </Text>
          <Image
            src={Blocks}
            alt="3D green glass shapes"
            width="100%"
            objectFit="cover"
          />
        </Container>
      </Box>
      {/* FROM CHAOS TO CLARITY SECTION */}
      <Container maxW="container.xl" py={{ base: 12, md: 20 }}>
        <Box display={{ md: 'flex' }} gap={12} justifyContent="space-between">
          <Box maxW="524px">
            <Text
              fontWeight="bold"
              fontSize={{ base: 'xl', md: '40px' }}
              mb={4}
              textColor={'text.darkBlue.900'}>
              FROM CHAOS TO CLARITY: <br />A NEW WAY TO MAKE MARKETING DECISIONS
            </Text>
          </Box>
          <Box
            maxW="476px"
            color="gray.700"
            fontSize={'15px'}
            lineHeight={'24px'}>
            <Text mb={2}>
              Traditional marketing tools are slow, biased, and disconnected.
              They create noise instead of clarity—making it hard for teams to
              act with confidence.
            </Text>
            <Text mb={2}>
              We built Merkatus.io to change that. By truly revealing sources of
              strength or drag, you can react with insight, and move faster with
              impact.
            </Text>
            <Text mb={2}>
              We believe in clarity over noise. By giving teams tools they
              actually love to use. And by building software that&apos;s
              intuitive, human-centric, and beautifully simple.
            </Text>
            <Text mb={2}>
              Our goal is simple: <br />
              Empower everyone to make smarter, faster, and more confident
              decisions every single day.
            </Text>
          </Box>
        </Box>
      </Container>

      {/* OBSESSED WITH CLARITY SECTION */}
      <Container maxW="container.xl" py={{ base: 12, md: 20 }}>
        <Box display={{ md: 'flex' }} gap={12} justifyContent="space-between">
          <Box maxW="524px">
            <Text
              fontWeight="bold"
              fontSize={{ base: 'xl', md: '40px' }}
              mb={4}
              textTransform={'uppercase'}
              textColor={'text.darkBlue.900'}>
              Obsessed with clarity. <br />
              Built for impact.
            </Text>
          </Box>
          <Box
            maxW="476px"
            color="gray.700"
            fontSize={'15px'}
            lineHeight={'24px'}>
            <Text mb={2}>
              Merkatus is built by a distributed team of analysts, data
              scientists, and engineers who care deeply about one thing: making
              marketing decisions smarter, faster, and clearer.
            </Text>
            <Text mb={2}>
              We don&apos;t just write code-we shape tools that help businesses
              untangle complex data, uncover real insights, and act with
              confidence. Every model we train, every chart we generate, every
              line of logicis built with precision.
            </Text>
            <Text mb={2}>
              Because in marketing, every decision matters. And behind every
              decision, there should be clarity, not chaos.
            </Text>
            <Text mb={2}>
              From data ingestion to budget recommendations, we make sure
              everything just works, so your team can focus on what to do next,
              not what went wrong.
            </Text>
          </Box>
        </Box>
      </Container>

      {/* ABOUT ME SECTION - Joona Käyhkö */}
      <Container maxW="container.xl" py={{ base: 8, md: 16 }}>
        <Box
          display={{ base: 'block', md: 'flex' }}
          gap={{ base: 8, md: 12 }}
          alignItems="stretch"
          minHeight={'100%'}>
          {/* Left column - Photo and text */}
          <Box flex="1">
            <Image
              src={JoonaPhotoUrl}
              alt="Joona Käyhkö"
              borderRadius="16px"
              objectFit="cover"
              mx="auto"
              mb={4}
              boxShadow="md"
            />
            <Text
              fontSize={'21px'}
              fontWeight={700}
              mb={1}
              color="text.darkBlue.900">
              Joona Käyhkö
            </Text>
            <Text fontSize={'18px'} fontWeight={500} color="text.darkBlue.900">
              Founder & CEO, Merkatus.io
            </Text>
            <Text mt={6} mb={4}>
              I&apos;m Joona Käyhkö Founder & CEO of Merkatus.io. With over a
              decade in marketing, media, and analytics — including 5+ years as
              CTO at a leading media agency — I&apos;ve seen firsthand how
              difficult it is for marketers to connect spend with results and
              act with confidence.
            </Text>

            <Text mb={4}>
              I founded Merkatus.io to change that, to bring fast, transparent,
              AI-powered modeling to marketing teams that need clarity, not
              complexity.
            </Text>

            <Text mb={4}>
              While the platform is shaped by deep industry experience,
              it&apos;s powered by a team of expert data scientists and
              engineers focused on building a scalable, product-led experience
              for every customer.
            </Text>

            <Text mb={4}>
              Merkatus.io isn&apos;t just another analytics dashboard or
              modeling tool, it&apos;s a decision-making infrastructure, built
              for marketing leaders.
            </Text>

            <Box mb={4}>
              <Box display="flex" alignItems="center" mb={2}>
                <Icon iconName="brain" width="24px" height="24px" mr={3} />
                <Text>
                  Built by marketers, powered by data scientists & developers
                </Text>
              </Box>
              <Box display="flex" alignItems="center" mb={2}>
                <Icon iconName="scale" width="24px" height="24px" mr={3} />
                <Text>Independent, transparent, and platform-neutral</Text>
              </Box>
              <Box display="flex" alignItems="center" mb={2}>
                <Icon iconName="scenario" width="24px" height="24px" mr={3} />
                <Text>Designed for clarity, speed, and action</Text>
              </Box>
            </Box>

            <Box>
              <Text mb={2}>
                <Icon iconName="link" width="24px" height="24px" mr={3} />{' '}
                Connect with me on{' '}
                <Text
                  as="a"
                  href="https://fi.linkedin.com/in/joona-k%C3%A4yhk%C3%B6-ab093659"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="blue.500"
                  textDecoration="underline"
                  cursor="pointer"
                  _hover={{ color: 'blue.600' }}>
                  LinkedIn
                </Text>
              </Text>
              <Text>
                <Icon iconName="mail-up" width="24px" height="24px" mr={3} />{' '}
                Reach our team at{' '}
                <Text
                  as="a"
                  href="mailto:support@merkatus.io"
                  color="blue.500"
                  textDecoration="underline"
                  cursor="pointer"
                  _hover={{ color: 'blue.600' }}>
                  support@merkatus.io
                </Text>
              </Text>
            </Box>
          </Box>

          {/* Right column - Statistics */}
          <Box
            flex="1"
            display={'flex'}
            justifyContent={'flex-end'}
            alignItems={'stretch'}
            mt={{ base: 8, md: 0 }}>
            {/* Statistics section */}
            <Box
              bg="background.lightGreen"
              width={{ base: '100%', md: '376px' }}
              borderRadius="16px"
              border="1px solid"
              borderColor={'border.lightGreen'}
              textAlign="center"
              p={{ base: 6, md: '36px' }}
              display={'flex'}
              flexDirection={'column'}
              height={'100%'}>
              <Box
                display={'flex'}
                flexDirection={'column'}
                height={'100%'}
                gap={{ base: 4, md: '56px' }}>
                <Box
                  flex="1"
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'center'}>
                  <Text
                    fontSize={{ base: '40px', md: '56px' }}
                    fontWeight="600"
                    color="text.darkBlue.800"
                    mb={{ base: 4, md: '36px' }}>
                    20+
                  </Text>
                  <Text
                    fontSize={{ base: '18px', md: '21px' }}
                    color="text.darkBlue.800"
                    fontWeight={500}>
                    Combined years
                    <br /> of experience
                  </Text>
                </Box>
                <Box
                  flex="1"
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'center'}>
                  <Text
                    fontSize={{ base: '40px', md: '56px' }}
                    fontWeight="600"
                    color="text.darkBlue.800">
                    20+
                  </Text>
                  <Text
                    fontSize={{ base: '18px', md: '21px' }}
                    color="text.darkBlue.800"
                    fontWeight={500}>
                    Modeling cases
                  </Text>
                </Box>
                <Box
                  flex="1"
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'center'}>
                  <Text
                    fontSize={{ base: '40px', md: '56px' }}
                    fontWeight="600"
                    color="text.darkBlue.800">
                    8
                  </Text>
                  <Text
                    fontSize={{ base: '18px', md: '21px' }}
                    color="text.darkBlue.800"
                    fontWeight={500}>
                    Employees, advisors,
                    <br /> partners
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>

      <Box py={24} px={4} bg={'lightGray'}>
        <HStack
          maxW="6xl"
          mx="auto"
          alignItems="flex-start"
          spacing={12}
          flexDir={isMobile ? 'column' : 'row'}>
          <VStack align="start" spacing={4} flex="1">
            <Heading
              size="xl"
              fontFamily="marketingPage"
              textColor={'text.darkBlue.900'}>
              ALWAYS NEAR. <br /> ALWAYS THINKING AHEAD
            </Heading>
            <Text maxWidth={'451px'} fontSize={15} lineHeight={'24px'}>
              We&apos;re headquartered in Helsinki, Finland—but our tools
              empower teams worldwide.
            </Text>
          </VStack>

          <Box flex="1">
            <Image
              src={BuildingImageUrl}
              alt="Merkatus.io Building"
              borderRadius="md"
              objectFit="cover"
              w="100%"
              maxH="352px"
            />
            <Box mt={4}>
              <MerkatusLogo theme="dark" />
              <Text fontSize={15} mt={4}>
                Merkatus.io Oy, Helsinki, Finland
              </Text>
              <Text fontSize={15} mt={1}>
                support@merkatus.io
              </Text>
            </Box>
          </Box>
        </HStack>
      </Box>
    </Box>
  );
};

export default CompanyPage;
